import type { AppError, MediaKind } from './types';
import { makeError } from './errors';

/** Invisible / bidi / control characters that enable look-alike URLs. D01-F02 */
const INVISIBLE_RANGES: [number, number][] = [
  [0x0000, 0x001f],
  [0x007f, 0x009f],
  [0x200b, 0x200f],
  [0x202a, 0x202e],
  [0x2060, 0x2064],
  [0x2066, 0x2069],
  [0xfeff, 0xfeff],
];
const INVISIBLE = new RegExp(
  `[${INVISIBLE_RANGES.map(([a, b]) => `${String.fromCodePoint(a)}-${String.fromCodePoint(b)}`).join('')}]`,
  'g',
);

const LOOPBACK = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

/** Hosts whose URLs are web pages, not files. ALG-077 */
const STREAMING_PAGE_HOSTS = [
  'youtube.com',
  'youtu.be',
  'vimeo.com',
  'netflix.com',
  'twitch.tv',
  'tiktok.com',
  'instagram.com',
  'facebook.com',
  'x.com',
  'twitter.com',
  'dailymotion.com',
  'spotify.com',
  'primevideo.com',
  'disneyplus.com',
  'hulu.com',
];

export interface ParsedSource {
  url: URL;
  /** Origin + path only: never contains query or hash values. */
  safeDisplay: string;
  host: string;
  /** True when the hostname uses IDN/punycode and might be a look-alike. */
  isPunycode: boolean;
  /** True when the query string looks like it carries a bearer token / signature. */
  looksSigned: boolean;
  inferredName: string;
  extension: string;
}

export type ParseResult = { ok: true; source: ParsedSource } | { ok: false; error: AppError };

const SIGNED_PARAM =
  /^(x-amz-|x-goog-|signature|sig|token|expires|se|sp|sv|sr|policy|key-pair-id|hdnts|hmac|auth|access_token)/i;

export function sanitizePastedUrl(raw: string): string {
  return raw.replace(INVISIBLE, '').trim();
}

export function parseSourceUrl(raw: string): ParseResult {
  const cleaned = sanitizePastedUrl(raw);
  if (cleaned === '') return { ok: false, error: makeError('INVALID_URL', { reason: 'empty' }) };
  if (/\s/.test(cleaned))
    return { ok: false, error: makeError('INVALID_URL', { reason: 'whitespace' }) };

  let url: URL;
  try {
    url = new URL(cleaned);
  } catch {
    return { ok: false, error: makeError('INVALID_URL', { reason: 'unparseable' }) };
  }

  const scheme = url.protocol;
  if (scheme !== 'https:' && scheme !== 'http:') {
    return {
      ok: false,
      error: makeError('UNSUPPORTED_SCHEME', { scheme: scheme.replace(':', '') }),
    };
  }
  if (scheme === 'http:' && !LOOPBACK.has(url.hostname)) {
    return {
      ok: false,
      error: makeError('UNSUPPORTED_SCHEME', { scheme: 'http', reason: 'insecure' }),
    };
  }
  if (url.username !== '' || url.password !== '') {
    return { ok: false, error: makeError('CREDENTIALS_IN_URL') };
  }

  const host = url.hostname.toLowerCase();
  if (STREAMING_PAGE_HOSTS.some((h) => host === h || host.endsWith(`.${h}`))) {
    return { ok: false, error: makeError('WEBPAGE_NOT_FILE', { host }) };
  }

  const inferredName = safeFilename(lastPathSegment(url.pathname));
  const extension = extensionOf(inferredName);
  const looksSigned = [...url.searchParams.keys()].some((k) => SIGNED_PARAM.test(k));

  return {
    ok: true,
    source: {
      url,
      safeDisplay: redactedUrl(url),
      host,
      isPunycode: host.split('.').some((l) => l.startsWith('xn--')),
      looksSigned,
      inferredName,
      extension,
    },
  };
}

/** Never includes query or fragment values. TYPE-037, ALG-079 */
export function redactedUrl(url: URL): string {
  const path =
    url.pathname.length > 64
      ? `${url.pathname.slice(0, 30)}…${url.pathname.slice(-30)}`
      : url.pathname;
  const q = url.search !== '' ? '?…' : '';
  return `${url.protocol}//${url.host}${path}${q}`;
}

/** Redact any URL-looking substrings in free text (error strings, logs). ALG-059 */
export function redactText(text: string): string {
  return text.replace(/https?:\/\/[^\s"'<>)]+/gi, (m) => {
    try {
      return redactedUrl(new URL(m));
    } catch {
      return '[url]';
    }
  });
}

function lastPathSegment(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  const last = parts[parts.length - 1] ?? '';
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

const RESERVED_NAMES = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\..*)?$/i;

/** Strip path separators, control characters and reserved names. D01-F08 */
export function safeFilename(name: string, fallback = 'download'): string {
  let n = name
    .replace(INVISIBLE, '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_')
    .replace(/^\.+/, '')
    .replace(/[. ]+$/, '')
    .trim();
  if (n === '' || RESERVED_NAMES.test(n)) n = fallback;
  if (n.length > 120) {
    const ext = extensionOf(n);
    const stem = n.slice(0, 120 - (ext ? ext.length + 1 : 0));
    n = ext ? `${stem}.${ext}` : stem;
  }
  return n;
}

export function extensionOf(name: string): string {
  const i = name.lastIndexOf('.');
  if (i <= 0 || i === name.length - 1) return '';
  return name.slice(i + 1).toLowerCase();
}

/**
 * Parse Content-Disposition for a filename. Prefers RFC 5987 filename*,
 * falls back to filename=, returns null on malformed input. D01-F08
 */
export function parseContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header);
  if (star) {
    const charset = (star[1] ?? 'utf-8').toLowerCase();
    const encoded = (star[2] ?? '').trim();
    try {
      if (charset === 'utf-8' || charset === '') return safeFilename(decodeURIComponent(encoded));
      if (charset === 'iso-8859-1') {
        const bytes = encoded.replace(/%([0-9a-f]{2})/gi, (_m, h: string) =>
          String.fromCharCode(parseInt(h, 16)),
        );
        return safeFilename(bytes);
      }
    } catch {
      /* malformed percent-encoding: fall through to the plain filename */
    }
  }
  const quoted = /filename\s*=\s*"((?:[^"\\]|\\.)*)"/i.exec(header);
  if (quoted?.[1]) return safeFilename(quoted[1].replace(/\\(.)/g, '$1'));
  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  if (bare?.[1]) return safeFilename(bare[1].trim());
  return null;
}

const EXT_KIND: Record<string, MediaKind> = {
  mp4: 'video',
  m4v: 'video',
  mov: 'video',
  webm: 'video',
  mkv: 'video',
  avi: 'video',
  ogv: 'video',
  mp3: 'audio',
  m4a: 'audio',
  aac: 'audio',
  wav: 'audio',
  flac: 'audio',
  ogg: 'audio',
  oga: 'audio',
  opus: 'audio',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
  avif: 'image',
  svg: 'image',
  bmp: 'image',
  ico: 'image',
  pdf: 'pdf',
  txt: 'text',
  log: 'text',
  md: 'markdown',
  markdown: 'markdown',
  json: 'json',
  csv: 'csv',
  tsv: 'csv',
  xml: 'xml',
  docx: 'docx',
  xlsx: 'xlsx',
  zip: 'archive',
};

export function kindFromExtension(ext: string): MediaKind {
  return EXT_KIND[ext.toLowerCase()] ?? 'unknown';
}

export function kindFromMime(mime: string | null): MediaKind {
  if (!mime) return 'unknown';
  const m = mime.split(';')[0]!.trim().toLowerCase();
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';
  if (m.startsWith('image/')) return 'image';
  if (m === 'application/pdf') return 'pdf';
  if (m === 'application/json') return 'json';
  if (m === 'text/markdown') return 'markdown';
  if (m === 'text/csv' || m === 'text/tab-separated-values') return 'csv';
  if (m === 'text/xml' || m === 'application/xml') return 'xml';
  if (m === 'text/plain') return 'text';
  if (m === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
    return 'docx';
  if (m === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'xlsx';
  if (m === 'application/zip') return 'archive';
  return 'unknown';
}

/** Is the declared MIME a web page rather than a file? ALG-077 */
export function isHtmlMime(mime: string | null): boolean {
  if (!mime) return false;
  const m = mime.split(';')[0]!.trim().toLowerCase();
  return m === 'text/html' || m === 'application/xhtml+xml';
}

/** Hash origin+path only (never query values) so signed URLs don't leak into storage. TYPE-039 */
export async function urlFingerprint(url: URL): Promise<string> {
  const data = new TextEncoder().encode(`${url.origin}${url.pathname}`);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest).slice(0, 12), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('');
}
